import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';

const differentiators = [
  'Shipped platform, Semantic HR includes the semantic layer, Cortex copilot, and governed Power BI bookmarks built and handed off in your Snowflake account',
  'End report hunting, no more scrolling through disparate Power BI workspaces, guessing bookmarks, or manually setting filters for every follow-up',
  'Dynamic analytics that collapse assumptions into governed evidence during the conversation, not days later',
  'Progressive inquiry, current state → trends → multi-domain follow-ups → plan vs actual → sensitivity & RIF scenarios',
  'Split-screen copilot, governed answers and embedded Power BI dashboards activated per question',
  'Expandable semantic layer and report library, grows with your HR analytics program, no fixed ceiling',
  'Consistent HR definitions across Workday, SuccessFactors, dashboards, and natural language queries',
  'Snowflake compute by design, role-authorized warehouses, governed metric views, and architecture that compresses analytic operations vs ad-hoc exploration',
  'Enterprise security with masking, row access policies, and domain roles for sensitive workforce data',
];

const useCases = [
  { title: 'Current State & Headcount', desc: 'Active headcount, FTE, and org breakdowns, the baseline every workforce conversation starts from.' },
  { title: 'Attrition & Retention', desc: 'Voluntary and involuntary trends, cohort analysis, and retention drivers, follow up naturally from headcount questions.' },
  { title: 'Hiring & Recruiting Capacity', desc: 'Open requisitions, pipeline stages, pass-through rates, and time-to-fill, connect attrition signals to hiring response.' },
  { title: 'Workforce Plan vs Actual', desc: 'Planned vs actual headcount and labor cost variance by business unit, bridge HR operations with FP&A.' },
  { title: 'Plan Sensitivity Analysis', desc: 'What-if on attrition, hiring velocity, backfill rules, and headcount targets, see FTE and labor cost impact against the approved plan.' },
  { title: 'RIF & Restructuring Assessments', desc: 'Model reduction-in-force and reorg scenarios by department, country, or BU, governed access to workforce and compensation context.' },
  { title: 'Growth & Forecast Scenarios', desc: 'Project headcount and labor cost based on current movement trends and plan assumptions when the priority is expansion, not contraction.' },
  { title: 'DEI & Org Design', desc: 'Representation metrics, pay equity patterns, spans of control, and manager structure, for board and reorg decisions.' },
];

export default function WhyDifferent() {
  return (
    <div id="use-cases" className="py-16 md:py-20 scroll-mt-24">
      <div className="container-x space-y-16 lg:space-y-20">
        <div>
          <div className="max-w-3xl mb-8">
            <p className="eyebrow">Differentiation</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">
              Why Workforce Leaders Choose Semantic HR
            </h2>
            <p className="mt-4 text-lg text-ink-600 leading-relaxed">
              Purpose-built for fast-moving workforce decisions, not static dashboard libraries, report
              archaeology, or generic enterprise chatbots.
            </p>
          </div>
          <div className="rounded-2xl border border-ink-200 bg-surface-muted p-6 sm:p-8">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {differentiators.map((point) => (
                <li key={point} className="flex items-start gap-2.5 text-sm text-ink-700 leading-relaxed">
                  <CheckIcon className="h-4 w-4 text-brand-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div>
          <div className="max-w-3xl mb-8">
            <p className="eyebrow">Use cases</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">
              From Current State to Workforce Forecast
            </h2>
            <p className="mt-4 text-lg text-ink-600 leading-relaxed">
              Scenarios that connect in real conversations, and expand as you add new metrics and reporting
              domains to the platform.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5 max-w-5xl">
            {useCases.map((uc) => (
              <div key={uc.title} className="flex gap-4 items-start border-b border-ink-100 pb-5">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700">
                  {uc.title.charAt(0)}
                </span>
                <div>
                  <p className="text-sm font-semibold text-ink-900">{uc.title}</p>
                  <p className="mt-1 text-sm text-ink-600 leading-relaxed">{uc.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <a href="#metrics" className="btn-secondary text-sm inline-flex">
              Browse governed KPIs in the HR metrics catalog →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
